import {
  addDays,
  formatDateRu,
  targetWeekFor,
  weekDays,
  weekStart as weekStartOf,
  type ScheduleAssignment,
  type ScheduleIssue,
} from '@pms/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuthStore, useCan } from '../auth/store.js';
import { request } from '../sync/api.js';
import { enqueue } from '../sync/outbox.js';
import { syncNow } from '../sync/engine.js';

/**
 * Сетка графика: участок → агрегаты и смены по дням недели.
 *
 * Система помогает, но не решает: автоматического составления графика нет.
 * Красная подсветка — так лучше не делать, жёлтая — можно, но посмотрите.
 */

interface WeekResponse {
  weekStart: string;
  status: string | null;
  publishedAt: string | null;
  assignments: ScheduleAssignment[];
  issues: ScheduleIssue[];
  users: Array<{ id: string; fullName: string }>;
  machines: Array<{
    id: string;
    siteId: string;
    name: string;
    minOperators: number;
    maxOperators: number;
  }>;
  shiftsPerUserWeek: Record<string, number>;
  shiftsPerUserMonth: Record<string, number>;
}

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

export function ScheduleScreen(): JSX.Element {
  const user = useAuthStore((state) => state.user);
  const canManage = useCan('schedule:manage');
  const [weekStart, setWeekStart] = useState(() => targetWeekFor(new Date().toISOString().slice(0, 10)));
  const [data, setData] = useState<WeekResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cell, setCell] = useState<{ machineId: string; date: string; shiftNumber: number } | null>(null);

  const load = useCallback(async (week: string): Promise<void> => {
    setError(null);
    try {
      setData(
        await request<WeekResponse>(
          `/api/schedule/week?weekStart=${week}`,
          { method: 'GET' },
          20_000,
        ),
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить график');
    }
  }, []);

  useEffect(() => {
    void load(weekStart);
  }, [weekStart, load]);

  if (!data) {
    return (
      <div className="min-h-screen px-4 py-6">
        <p className="text-lg text-dim">{error ?? 'Загружаю график…'}</p>
        <Link to="/" className="mt-4 inline-block rounded-xl bg-muted px-4 py-3 text-base">
          Назад
        </Link>
      </div>
    );
  }

  const days = weekDays(weekStart);
  const published = data.status === 'PUBLISHED';

  const issuesFor = (machineId: string, date: string, shiftNumber: number): ScheduleIssue[] =>
    data.issues.filter(
      (issue) =>
        issue.machineId === machineId && issue.date === date && issue.shiftNumber === shiftNumber,
    );

  const peopleIn = (machineId: string, date: string, shiftNumber: number): ScheduleAssignment[] =>
    data.assignments.filter(
      (assignment) =>
        assignment.machineId === machineId &&
        assignment.date === date &&
        assignment.shiftNumber === shiftNumber,
    );

  const applyLocal = (assignments: ScheduleAssignment[]): void => {
    setData({ ...data, assignments });
  };

  const toggleUser = (userId: string): void => {
    if (!cell) return;
    const machine = data.machines.find((item) => item.id === cell.machineId);
    if (!machine) return;

    const exists = data.assignments.some(
      (assignment) =>
        assignment.machineId === cell.machineId &&
        assignment.date === cell.date &&
        assignment.shiftNumber === cell.shiftNumber &&
        assignment.userId === userId,
    );

    applyLocal(
      exists
        ? data.assignments.filter(
            (assignment) =>
              !(
                assignment.machineId === cell.machineId &&
                assignment.date === cell.date &&
                assignment.shiftNumber === cell.shiftNumber &&
                assignment.userId === userId
              ),
          )
        : [
            ...data.assignments,
            {
              siteId: machine.siteId,
              machineId: cell.machineId,
              date: cell.date,
              shiftNumber: cell.shiftNumber,
              userId,
            },
          ],
    );
  };

  const save = async (): Promise<void> => {
    if (!user) return;
    setBusy(true);
    try {
      if (published) {
        const reason = window.prompt('Причина изменения опубликованного графика:')?.trim();
        if (!reason || reason.length < 3) return;
        await enqueue({
          type: 'SCHEDULE_CHANGED',
          aggregateType: 'WEEK_PLAN',
          aggregateId: weekStart,
          userId: user.id,
          payload: { weekStart, assignments: data.assignments, reason },
        });
      } else {
        await enqueue({
          type: 'SCHEDULE_DRAFT_UPDATED',
          aggregateType: 'WEEK_PLAN',
          aggregateId: weekStart,
          userId: user.id,
          payload: { weekStart, assignments: data.assignments },
        });
      }
      await syncNow();
      await load(weekStart);
    } finally {
      setBusy(false);
    }
  };

  const publish = async (): Promise<void> => {
    if (!user) return;
    setBusy(true);
    try {
      await enqueue({
        type: 'SCHEDULE_PUBLISHED',
        aggregateType: 'WEEK_PLAN',
        aggregateId: weekStart,
        userId: user.id,
        payload: { weekStart },
      });
      await syncNow();
      await load(weekStart);
    } finally {
      setBusy(false);
    }
  };

  const startCollection = async (): Promise<void> => {
    if (!user) return;
    setBusy(true);
    try {
      // Сбор идёт на СЛЕДУЮЩУЮ неделю, а не на ту, что открыта на экране.
      // Раньше бралась просматриваемая: администратор, листавший текущую
      // неделю, открывал сбор на неё же — и операторы, чей экран всегда
      // спрашивает про следующую, получали отказ «сбор не открыт».
      const today = new Date().toISOString().slice(0, 10);
      const collectFor = targetWeekFor(today);
      // Приём закрывается накануне начала недели: в воскресенье текущей
      const closesAt = new Date(`${addDays(weekStartOf(today), 6)}T23:59:00`);
      await enqueue({
        type: 'WISH_COLLECTION_STARTED',
        aggregateType: 'WEEK_PLAN',
        aggregateId: collectFor,
        userId: user.id,
        payload: { weekStart: collectFor, closesAt: closesAt.toISOString() },
      });
      await syncNow();
      // Показываем ту неделю, на которую только что открыли сбор: иначе
      // администратор остаётся на прежней и не видит, что получилось
      setWeekStart(collectFor);
      await load(collectFor);
    } finally {
      setBusy(false);
    }
  };

  const bySite = new Map<string, typeof data.machines>();
  for (const machine of data.machines) {
    bySite.set(machine.siteId, [...(bySite.get(machine.siteId) ?? []), machine]);
  }

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">График смен</h1>
        <Link to="/" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-touch">
        <button
          type="button"
          onClick={() => setWeekStart(addDays(weekStart, -7))}
          className="min-h-touch rounded-xl bg-muted px-4 text-base font-semibold"
        >
          ← неделя
        </button>
        <span className="text-lg font-semibold">
          {formatDateRu(weekStart)} — {formatDateRu(addDays(weekStart, 6))}
        </span>
        <button
          type="button"
          onClick={() => setWeekStart(addDays(weekStart, 7))}
          className="min-h-touch rounded-xl bg-muted px-4 text-base font-semibold"
        >
          неделя →
        </button>
        <span
          className={`rounded-lg px-3 py-2 text-base font-semibold ${
            published ? 'bg-emerald-700 text-white' : 'bg-amber-500 text-black'
          }`}
        >
          {data.status === 'PUBLISHED'
            ? 'опубликован'
            : data.status === 'COLLECTING'
              ? 'идёт сбор пожеланий'
              : 'черновик'}
        </span>
      </div>

      {/* Руководитель график смотрит, но не ведёт: кнопки правки ему не нужны,
          а сервер такие изменения от него всё равно не примет */}
      {canManage ? (
        <div className="mb-6 flex flex-wrap gap-touch">
          <button
            type="button"
            disabled={busy}
            onClick={() => void startCollection()}
            className="min-h-touch rounded-xl bg-muted px-5 text-base font-semibold disabled:opacity-40"
          >
            Запустить сбор пожеланий
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void save()}
            className="min-h-touch rounded-xl bg-sky-600 px-5 text-base font-semibold text-white disabled:opacity-40"
          >
            {published ? 'Сохранить изменение' : 'Сохранить черновик'}
          </button>
          {!published && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void publish()}
              className="min-h-touch rounded-xl bg-emerald-600 px-5 text-base font-semibold text-white disabled:opacity-40"
            >
              Опубликовать
            </button>
          )}
        </div>
      ) : (
        <p className="mb-6 rounded-xl bg-card shadow-card px-4 py-3 text-base text-dim">
          Только просмотр. График ведёт администратор.
        </p>
      )}

      {[...bySite.entries()].map(([siteId, machines]) => (
        <section key={siteId} className="mb-8 overflow-x-auto">
          <table className="w-full min-w-[720px] border-separate border-spacing-1">
            <thead>
              <tr>
                <th className="text-left text-base text-dim">Агрегат / смена</th>
                {days.map((day, index) => (
                  <th key={day} className="text-base text-dim">
                    {WEEKDAYS[index]} {formatDateRu(day).slice(0, 5)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {machines.flatMap((machine) =>
                [1, 2, 3].map((shiftNumber) => {
                  const hasShift = data.issues.some(
                    (issue) => issue.machineId === machine.id && issue.shiftNumber === shiftNumber,
                  ) || data.assignments.some(
                    (assignment) =>
                      assignment.machineId === machine.id && assignment.shiftNumber === shiftNumber,
                  );
                  if (!hasShift) return null;

                  return (
                    <tr key={`${machine.id}-${shiftNumber}`}>
                      <td className="whitespace-nowrap pr-2 text-base">
                        {machine.name} · {shiftNumber}
                      </td>
                      {days.map((day) => {
                        const people = peopleIn(machine.id, day, shiftNumber);
                        const cellIssues = issuesFor(machine.id, day, shiftNumber);
                        const hasError = cellIssues.some((issue) => issue.severity === 'error');
                        const hasWarning = cellIssues.some((issue) => issue.severity === 'warning');

                        return (
                          <td key={day}>
                            <button
                              type="button"
                              title={cellIssues.map((issue) => issue.message).join('\n')}
                              onClick={() =>
                                setCell({ machineId: machine.id, date: day, shiftNumber })
                              }
                              className={`h-full w-full rounded-lg px-2 py-3 text-left text-base ${
                                hasError
                                  ? 'bg-rose-50 text-rose-800 ring-1 ring-rose-200'
                                  : hasWarning
                                    ? 'bg-amber-700/60'
                                    : people.length > 0
                                      ? 'bg-muted'
                                      : 'bg-card shadow-card'
                              }`}
                            >
                              {people.length === 0
                                ? '—'
                                : people
                                    .map(
                                      (assignment) =>
                                        data.users
                                          .find((item) => item.id === assignment.userId)
                                          ?.fullName.split(' ')[0] ?? '?',
                                    )
                                    .join(', ')}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                }),
              )}
            </tbody>
          </table>
        </section>
      ))}

      {cell && (
        <section className="mb-6 rounded-xl bg-card shadow-card p-4">
          <h2 className="mb-3 text-lg font-semibold">
            {data.machines.find((machine) => machine.id === cell.machineId)?.name} ·{' '}
            {formatDateRu(cell.date)} · смена {cell.shiftNumber}
          </h2>
          <div className="flex flex-col gap-touch">
            {data.users.map((person) => {
              const assigned = peopleIn(cell.machineId, cell.date, cell.shiftNumber).some(
                (assignment) => assignment.userId === person.id,
              );
              return (
                <button
                  key={person.id}
                  type="button"
                  onClick={() => toggleUser(person.id)}
                  className={`flex min-h-touch items-center justify-between rounded-xl px-4 py-3 text-left text-base font-semibold ${
                    assigned ? 'bg-sky-700 text-white' : 'bg-muted'
                  }`}
                >
                  <span>{person.fullName}</span>
                  <span className="text-body">
                    неделя {data.shiftsPerUserWeek[person.id] ?? 0} · месяц{' '}
                    {data.shiftsPerUserMonth[person.id] ?? 0}
                  </span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setCell(null)}
            className="mt-4 rounded-xl bg-muted px-5 py-3 text-base font-semibold"
          >
            Закрыть
          </button>
        </section>
      )}

      <section className="rounded-xl bg-card shadow-card p-4">
        <h2 className="mb-3 text-lg font-semibold">Замечания ({data.issues.length})</h2>
        <ul className="flex flex-col gap-1 text-base">
          {data.issues.slice(0, 40).map((issue, index) => (
            <li
              key={index}
              className={issue.severity === 'error' ? 'text-red-300' : 'text-amber-700'}
            >
              {issue.date ? `${formatDateRu(issue.date).slice(0, 5)} · ` : ''}
              {issue.userId
                ? `${data.users.find((item) => item.id === issue.userId)?.fullName ?? ''} — `
                : ''}
              {issue.message}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
